import React, { useState, useEffect } from 'react';
import { Card } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { toast } from 'sonner';
import api from '../lib/api';
import { 
  Clock, Calendar, User, MapPin, Coffee, Calculator,
  CheckCircle, AlertCircle, Send, Loader2, FileText,
  ChevronLeft, ChevronRight, Download
} from 'lucide-react';

export default function TimesheetForm() {
  const [loading, setLoading] = useState(true);
  const [timesheet, setTimesheet] = useState(null);
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [editingBreak, setEditingBreak] = useState(null);
  const [breakValue, setBreakValue] = useState('');

  useEffect(() => {
    // Initialize with last 14 days
    const today = new Date();
    const twoWeeksAgo = new Date(today);
    twoWeeksAgo.setDate(today.getDate() - 14);
    
    setEndDate(today.toISOString().split('T')[0]);
    setStartDate(twoWeeksAgo.toISOString().split('T')[0]);
  }, []);

  useEffect(() => {
    if (startDate && endDate) {
      fetchTimesheet();
    }
  }, [startDate, endDate]);

  const fetchTimesheet = async () => {
    setLoading(true);
    try {
      const response = await api.get('/shift-toolkit/timesheet', {
        params: { start_date: startDate, end_date: endDate }
      });
      setTimesheet(response.data);
    } catch (error) {
      console.error('Failed to fetch timesheet:', error);
      toast.error('Failed to load timesheet');
    } finally {
      setLoading(false);
    }
  };

  const handleUpdateBreak = async (shiftId) => {
    if (!breakValue && breakValue !== 0) {
      toast.error('Please enter break minutes');
      return;
    }

    try {
      await api.put(`/shift-toolkit/timesheet/${shiftId}/break`, {
        break_minutes: parseInt(breakValue)
      });
      toast.success('Break time updated');
      setEditingBreak(null);
      setBreakValue('');
      fetchTimesheet();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update break');
    }
  };

  const handleSubmitTimesheet = async () => {
    setSubmitting(true);
    try {
      const response = await api.post('/shift-toolkit/timesheet/submit', null, {
        params: { start_date: startDate, end_date: endDate }
      });
      toast.success(response.data.message);
      fetchTimesheet();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to submit timesheet');
    } finally {
      setSubmitting(false);
    }
  };

  const navigatePeriod = (direction) => {
    const start = new Date(startDate);
    const end = new Date(endDate);
    const days = direction === 'prev' ? -14 : 14;
    
    start.setDate(start.getDate() + days);
    end.setDate(end.getDate() + days);
    
    setStartDate(start.toISOString().split('T')[0]);
    setEndDate(end.toISOString().split('T')[0]);
  };

  const formatDate = (dateStr) => {
    return new Date(dateStr).toLocaleDateString('en-US', {
      weekday: 'short',
      month: 'short',
      day: 'numeric'
    });
  };

  const formatTime = (dateStr) => {
    if (!dateStr) return '--:--';
    return new Date(dateStr).toLocaleTimeString('en-US', {
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const formatHours = (hours) => {
    const h = Math.floor(hours);
    const m = Math.round((hours - h) * 60);
    return `${h}h ${m}m`;
  };

  const getStatusBadge = (status, approvalStatus) => {
    if (approvalStatus === 'approved') {
      return <span className="px-2 py-1 text-xs rounded-full bg-green-100 text-green-800 font-medium">Approved</span>;
    }
    if (status === 'pending_approval') {
      return <span className="px-2 py-1 text-xs rounded-full bg-amber-100 text-amber-800 font-medium">Pending</span>;
    }
    if (approvalStatus === 'needs_correction') {
      return <span className="px-2 py-1 text-xs rounded-full bg-red-100 text-red-800 font-medium">Needs Correction</span>;
    }
    return <span className="px-2 py-1 text-xs rounded-full bg-gray-100 text-gray-800 font-medium">Completed</span>;
  };

  const hasUnsubmittedShifts = timesheet?.entries?.some(
    e => e.clock_out_time && !['pending_approval', 'approved'].includes(e.status) && e.approval_status !== 'approved'
  );

  if (loading && !timesheet) {
    return (
      <div className="flex justify-center items-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-purple-600" />
      </div>
    );
  }

  return (
    <div className="space-y-6 max-w-4xl mx-auto">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="p-3 bg-purple-100 rounded-xl">
            <FileText className="w-8 h-8 text-purple-600" />
          </div>
          <div>
            <h1 className="text-2xl font-bold text-purple-900">Digital Timesheet</h1>
            <p className="text-gray-600">Review and submit your hours</p>
          </div>
        </div>
      </div>

      {/* Period Navigation */}
      <Card className="p-4 border-purple-100">
        <div className="flex items-center justify-between">
          <Button
            variant="ghost"
            onClick={() => navigatePeriod('prev')}
            className="text-purple-600"
            data-testid="prev-period-btn"
          >
            <ChevronLeft className="w-5 h-5 mr-1" />
            Previous
          </Button>
          
          <div className="flex items-center gap-4">
            <div className="flex items-center gap-2">
              <Label className="text-sm text-gray-500">From:</Label>
              <Input
                type="date"
                value={startDate}
                onChange={(e) => setStartDate(e.target.value)}
                className="w-40"
                data-testid="start-date-input"
              />
            </div>
            <div className="flex items-center gap-2">
              <Label className="text-sm text-gray-500">To:</Label>
              <Input
                type="date"
                value={endDate}
                onChange={(e) => setEndDate(e.target.value)}
                className="w-40"
                data-testid="end-date-input"
              />
            </div>
          </div>
          
          <Button
            variant="ghost"
            onClick={() => navigatePeriod('next')}
            className="text-purple-600"
            data-testid="next-period-btn"
          >
            Next
            <ChevronRight className="w-5 h-5 ml-1" />
          </Button>
        </div>
      </Card>

      {/* Summary Card */}
      {timesheet && (
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <Card className="p-4 border-purple-100 bg-purple-50">
            <div className="flex items-center gap-3">
              <Clock className="w-6 h-6 text-purple-600" />
              <div>
                <p className="text-sm text-gray-600">Total Hours</p>
                <p className="text-xl font-bold text-purple-900">{formatHours(timesheet.total_hours)}</p>
              </div>
            </div>
          </Card>
          
          <Card className="p-4 border-purple-100 bg-amber-50">
            <div className="flex items-center gap-3">
              <Coffee className="w-6 h-6 text-amber-600" />
              <div>
                <p className="text-sm text-gray-600">Total Breaks</p>
                <p className="text-xl font-bold text-amber-900">{timesheet.total_breaks_minutes}m</p>
              </div>
            </div>
          </Card>
          
          <Card className="p-4 border-purple-100 bg-green-50">
            <div className="flex items-center gap-3">
              <Calculator className="w-6 h-6 text-green-600" />
              <div>
                <p className="text-sm text-gray-600">Net Hours</p>
                <p className="text-xl font-bold text-green-900">{formatHours(timesheet.net_hours)}</p>
              </div>
            </div>
          </Card>
          
          <Card className="p-4 border-purple-100 bg-blue-50">
            <div className="flex items-center gap-3">
              <Calendar className="w-6 h-6 text-blue-600" />
              <div>
                <p className="text-sm text-gray-600">Shifts</p>
                <p className="text-xl font-bold text-blue-900">{timesheet.entries.length}</p>
              </div>
            </div>
          </Card>
        </div>
      )}

      {/* Timesheet Entries */}
      {timesheet && timesheet.entries.length === 0 ? (
        <Card className="p-8 text-center border-purple-100">
          <Clock className="w-12 h-12 text-gray-300 mx-auto mb-3" />
          <p className="text-gray-500">No shifts recorded in this period</p>
          <p className="text-sm text-gray-400 mt-1">Clock in/out from the Shift Toolkit to record your hours</p>
        </Card>
      ) : (
        <Card className="border-purple-100 overflow-hidden">
          <table className="w-full" data-testid="timesheet-table">
            <thead className="bg-purple-50">
              <tr>
                <th className="px-4 py-3 text-left text-sm font-semibold text-purple-900">Date</th>
                <th className="px-4 py-3 text-left text-sm font-semibold text-purple-900">Client</th>
                <th className="px-4 py-3 text-left text-sm font-semibold text-purple-900">Clock In</th>
                <th className="px-4 py-3 text-left text-sm font-semibold text-purple-900">Clock Out</th>
                <th className="px-4 py-3 text-left text-sm font-semibold text-purple-900">Break</th>
                <th className="px-4 py-3 text-left text-sm font-semibold text-purple-900">Total</th>
                <th className="px-4 py-3 text-left text-sm font-semibold text-purple-900">Status</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {timesheet?.entries.map((entry) => (
                <tr key={entry.id} className="hover:bg-gray-50" data-testid={`timesheet-row-${entry.id}`}>
                  <td className="px-4 py-3 text-sm">
                    <div className="flex items-center gap-2">
                      <Calendar className="w-4 h-4 text-gray-400" />
                      {formatDate(entry.shift_date)}
                    </div>
                  </td>
                  <td className="px-4 py-3 text-sm">
                    {entry.client_name || <span className="text-gray-400">--</span>}
                  </td>
                  <td className="px-4 py-3 text-sm font-medium text-green-700">
                    {formatTime(entry.clock_in_time)}
                  </td>
                  <td className="px-4 py-3 text-sm font-medium text-red-700">
                    {formatTime(entry.clock_out_time)}
                  </td>
                  <td className="px-4 py-3 text-sm">
                    {editingBreak === entry.id ? (
                      <div className="flex items-center gap-2">
                        <Input
                          type="number"
                          min="0"
                          max="480"
                          value={breakValue}
                          onChange={(e) => setBreakValue(e.target.value)}
                          className="w-16 h-8"
                          placeholder="mins"
                          data-testid={`break-input-${entry.id}`}
                        />
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={() => handleUpdateBreak(entry.id)}
                          className="h-8 px-2 text-green-600"
                        >
                          <CheckCircle className="w-4 h-4" />
                        </Button>
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={() => { setEditingBreak(null); setBreakValue(''); }}
                          className="h-8 px-2 text-gray-400"
                        >
                          ×
                        </Button>
                      </div>
                    ) : (
                      <button
                        onClick={() => {
                          if (entry.approval_status !== 'approved') {
                            setEditingBreak(entry.id);
                            setBreakValue(entry.break_minutes.toString());
                          }
                        }}
                        className={`flex items-center gap-1 ${
                          entry.approval_status !== 'approved' 
                            ? 'text-amber-600 hover:text-amber-800 cursor-pointer' 
                            : 'text-gray-600 cursor-default'
                        }`}
                        disabled={entry.approval_status === 'approved'}
                        data-testid={`break-btn-${entry.id}`}
                      >
                        <Coffee className="w-3 h-3" />
                        {entry.break_minutes}m
                      </button>
                    )}
                  </td>
                  <td className="px-4 py-3 text-sm font-semibold text-purple-900">
                    {formatHours(entry.total_hours)}
                  </td>
                  <td className="px-4 py-3 text-sm">
                    {getStatusBadge(entry.status, entry.approval_status)}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </Card>
      )}

      {/* Submit Button */}
      {timesheet && timesheet.entries.length > 0 && (
        <div className="flex justify-between items-center">
          <p className="text-sm text-gray-500">
            {hasUnsubmittedShifts ? (
              <span className="flex items-center gap-1 text-amber-600">
                <AlertCircle className="w-4 h-4" />
                You have shifts ready to submit for approval
              </span>
            ) : (
              <span className="flex items-center gap-1 text-green-600">
                <CheckCircle className="w-4 h-4" />
                All shifts submitted
              </span>
            )}
          </p>
          
          <Button
            onClick={handleSubmitTimesheet}
            disabled={submitting || !hasUnsubmittedShifts}
            className="bg-purple-600 hover:bg-purple-700 text-white"
            data-testid="submit-timesheet-btn"
          >
            {submitting ? (
              <Loader2 className="w-4 h-4 mr-2 animate-spin" />
            ) : (
              <Send className="w-4 h-4 mr-2" />
            )}
            Submit for Approval
          </Button>
        </div>
      )}

      {/* Help Text */}
      <Card className="p-4 border-purple-100 bg-purple-50">
        <h4 className="font-medium text-purple-900 mb-2">How Timesheets Work</h4>
        <ul className="text-sm text-gray-600 space-y-1">
          <li>• Hours are auto-calculated from your Clock In/Out times in the Shift Toolkit</li>
          <li>• Click on break time to edit (before approval)</li>
          <li>• Submit your timesheet for Admin review at the end of each pay period</li>
          <li>• Approved shifts will be exported to QuickBooks for payroll</li>
        </ul>
      </Card>
    </div>
  );
}
